import type { HomeyAPI, FlowFolder } from 'homey-api';

import { __DEV__ } from '../../lib/__dev__.js';
import { BaseApiStore, type FetchArgs } from '../BaseApiStore.ts';

export type FlowFolderProps = {
  [K in keyof FlowFolder as FlowFolder[K] extends Function ? never : K]: FlowFolder[K];
};

type FlowFoldersData = { [key: string]: FlowFolder };
type FlowFoldersById = { [key: string]: FlowFolderProps };

interface State {
  data: FlowFoldersData | null;
  byId: FlowFoldersById | null;
  isLoading: boolean;
  error: unknown;
}

export type FlowFoldersStoreState = State;

export class FlowFoldersStore extends BaseApiStore<State, FlowFolder> {
  constructor() {
    super('flow-folders');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ api, manager: api.flow, reference: this });
      if (abortController.signal.aborted) return;

      const flowFoldersPromise = api.flow.getFlowFolders({ $cache: cache });
      const [flowFoldersData] = await Promise.all([flowFoldersPromise]);

      if (abortController.signal.aborted) return;

      const flowFolders = Object.values(flowFoldersData);

      const data: FlowFoldersData = {};
      const byId: FlowFoldersById = {};

      for (const flowFolderInstance of flowFolders) {
        const flowFolderReference = { ...flowFolderInstance };

        data[flowFolderReference.id] = flowFolderInstance;
        byId[flowFolderReference.id] = flowFolderReference;
      }

      this.set({
        ...this.createInitialState(),
        data: data,
        byId: byId,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy(reason: string) {
    try {
      const api = this.getApi();
      this.disconnectManager({ api, manager: api.flow, reference: this }).catch(console.error);
    } catch (error) {}
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.flow.on('flowfolder.create', this.handleCreate);
    api.flow.on('flowfolder.update', this.handleUpdate);
    api.flow.on('flowfolder.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.flow.off('flowfolder.create', this.handleCreate);
    api.flow.off('flowfolder.update', this.handleUpdate);
    api.flow.off('flowfolder.delete', this.handleDelete);
  }

  getInstanceFromCache(flowFolder: { id: string }) {
    return this.getCache()[flowFolder.id];
  }

  getCache(): { [key: string]: FlowFolder } {
    const api = this.getApi();
    const cache = api.flow.__cache['flowfolder'];
    return cache;
  }

  handleCreate = (createdFlowFolder: FlowFolder) => {
    this.DEBUG && console.info(`create:flowfolder:${createdFlowFolder.id}`);
    const state = this.get();
    const flowFolderInstance = this.getInstanceFromCache(createdFlowFolder);

    if (flowFolderInstance == null) return;

    const flowFolderReference = { ...flowFolderInstance };

    this.set({
      data: {
        ...state.data,
        [createdFlowFolder.id]: flowFolderInstance,
      },
      byId: {
        ...state.byId,
        [createdFlowFolder.id]: flowFolderReference,
      },
    });
  };

  handleUpdate = (updatedFlowFolder: FlowFolder) => {
    this.DEBUG && console.info(`update:flowfolder:${updatedFlowFolder.id}`);

    this.addToQueue(updatedFlowFolder.id, updatedFlowFolder);

    this.batch((updateQueue) => {
      const state = this.get();

      const nextById = {
        ...state.byId,
      };

      let updateCount = 0;

      for (const [id, queuedFlowFolder] of Object.entries(updateQueue)) {
        const flowFolderInstance = this.getInstanceFromCache(queuedFlowFolder);

        if (state.data == null) {
          this.DEBUG && console.info(`update:flowfolder:missing-data`);
          continue;
        }

        if (flowFolderInstance == null || state.data[id] == null) {
          this.DEBUG && console.info(`update:flowfolder:unknown:${id}`);
          continue;
        }

        const flowFolderReference = { ...flowFolderInstance };

        nextById[id] = flowFolderReference;

        updateCount++;
      }

      this.DEBUG && console.info(`update:flowfolder:batch:count:${updateCount}`);

      updateCount > 0 &&
        this.set({
          byId: nextById,
        });
    });
  };

  handleDelete = (deletedFlowFolder: FlowFolder) => {
    this.DEBUG && console.info(`delete:flowfolder:${deletedFlowFolder.id}`);
    const state = this.get();

    if (state.data == null) {
      __DEV__ && console.log(`delete:flowfolder:missing-data`);
      return;
    }

    const flowFolderInstance = state.data[deletedFlowFolder.id];

    if (flowFolderInstance == null) return;

    const nextData = { ...state.data };
    const nextById = { ...state.byId };

    delete nextData[flowFolderInstance.id];
    delete nextById[flowFolderInstance.id];

    this.set({
      data: nextData,
      byId: nextById,
    });
  };

  restore() {
    const cache = this.getCache();
    const flowFolders = Object.values(cache ?? {});
    const data: FlowFoldersData = {};
    const byId: FlowFoldersById = {};

    for (const flowFolderInstance of flowFolders) {
      const flowFolderReference = { ...flowFolderInstance };
      data[flowFolderReference.id] = flowFolderInstance;
      byId[flowFolderReference.id] = flowFolderReference;
    }

    this.set({
      data: data,
      byId: byId,
    });
  }
}

export const flowFoldersStore = new FlowFoldersStore();
